import {
  Badge,
  Box,
  Button,
  Card,
  Group,
  List,
  Loader,
  SimpleGrid,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import type { ReactNode } from 'react';

import { AppShell } from '../components/app-shell/AppShell';
import { MotionSection, PageHero } from '../components/ui';
import { fetchJson, mutateJson } from '../lib/api';
import { useAuth } from '../lib/auth-context';
import { showToast } from '../lib/toast';

type ArtistSubscriptionDto = {
  id: string;
  tier: 'tourer' | 'world_tourer';
  state: 'active' | 'trial' | 'past_due' | 'paused' | 'cancelled';
  billingCycle: 'monthly' | 'annual';
  priceAmountMinor: number;
  priceCurrency: string;
  currentPeriodStart: number | null;
  currentPeriodEnd: number | null;
  nextChargeAt: number | null;
  lastChargeAt: number | null;
  cancelledAt: number | null;
  cancellationReason: string | null;
};

type ArtistBillingResponse = {
  ok: boolean;
  subscription: ArtistSubscriptionDto | null;
};

const QUERY_KEY = ['artist-billing'] as const;

export const Route = createFileRoute('/artist-billing')({
  component: ArtistBillingPage,
});

function ArtistBillingPage() {
  const queryClient = useQueryClient();
  const { capabilities } = useAuth();
  const isArtist = !!capabilities?.isArtist;

  const billing = useQuery({
    queryKey: QUERY_KEY,
    queryFn: () => fetchJson<ArtistBillingResponse>('/api/artist-billing'),
    enabled: isArtist,
  });

  const checkout = useMutation({
    mutationFn: (body: { tier: 'tourer' | 'world_tourer'; billingCycle: 'monthly' | 'annual' }) =>
      mutateJson<{ ok: boolean; subscriptionId: string }>('/api/artist-billing/checkout', body),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      showToast({ kind: 'success', body: 'You’re on. Your plan is active.' });
    },
    onError: (err) =>
      showToast({ kind: 'error', body: err instanceof Error ? err.message : 'Something went wrong.' }),
  });

  const cancel = useMutation({
    mutationFn: () => mutateJson<{ ok: boolean }>('/api/artist-billing/cancel'),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      showToast({ kind: 'success', body: 'Your plan has been cancelled.' });
    },
    onError: (err) =>
      showToast({ kind: 'error', body: err instanceof Error ? err.message : 'Something went wrong.' }),
  });

  const resume = useMutation({
    mutationFn: () => mutateJson<{ ok: boolean }>('/api/artist-billing/resume'),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      showToast({ kind: 'success', body: 'Welcome back. Your plan is active again.' });
    },
    onError: (err) =>
      showToast({ kind: 'error', body: err instanceof Error ? err.message : 'Something went wrong.' }),
  });

  if (!isArtist) {
    return (
      <AppShell tone="form">
        <Stack gap="lg">
          <PageHero
            eyebrow="Plan"
            title="Artist plans"
            lede="Set up your artist profile to explore touring plans."
          />
        </Stack>
      </AppShell>
    );
  }

  if (billing.isLoading) {
    return (
      <AppShell tone="form">
        <Loader />
      </AppShell>
    );
  }

  if (billing.error) {
    return (
      <AppShell tone="form">
        <Text c="red">We couldn't load your plan details. Try again shortly.</Text>
      </AppShell>
    );
  }

  const sub = billing.data?.subscription ?? null;
  const currentTier: 'roamer' | 'tourer' | 'world_tourer' = sub?.tier ?? 'roamer';

  return (
    <AppShell tone="content">
      <Stack gap="xl">
        <MotionSection>
          <PageHero
            eyebrow="Plan"
            title="Choose your touring plan"
            lede="Unlock Smart Match, international routing, and more as you grow."
            action={
              <Button variant="subtle" size="sm" onClick={() => window.history.back()}>
                ← Back
              </Button>
            }
          />
        </MotionSection>

        <TierCards
          currentTier={currentTier}
          subscriptionState={sub?.state ?? null}
          onUpgrade={(tier) => checkout.mutate({ tier, billingCycle: 'monthly' })}
          busy={checkout.isPending}
        />

        {sub && (
          <SubscriptionDetails
            sub={sub}
            onCancel={() => cancel.mutate()}
            onResume={() => resume.mutate()}
            cancelBusy={cancel.isPending}
            resumeBusy={resume.isPending}
          />
        )}
      </Stack>
    </AppShell>
  );
}

function TierCards({
  currentTier,
  subscriptionState,
  onUpgrade,
  busy,
}: {
  currentTier: 'roamer' | 'tourer' | 'world_tourer';
  subscriptionState: string | null;
  onUpgrade: (tier: 'tourer' | 'world_tourer') => void;
  busy: boolean;
}) {
  const isActive = subscriptionState === 'active' || subscriptionState === 'trial';

  return (
    <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="lg">
      <TierCard
        name="Roamer"
        price="Free"
        priceDetail="forever"
        isCurrent={currentTier === 'roamer'}
        features={[
          'Browse venues across Australia',
          'Send exchange requests',
          'Build your artist profile',
          'Ambassador referral rewards',
          'Tour routes (AU only)',
        ]}
        action={null}
      />
      <TierCard
        name="Tourer"
        price="$9"
        priceDetail="/month"
        isCurrent={currentTier === 'tourer' && isActive}
        highlighted
        features={[
          'Everything in Roamer',
          'Smart Match, get paired with compatible venues',
          'Smart Touring, optimised multi-stop routes',
        ]}
        action={
          currentTier === 'roamer' ? (
            <Button
              variant="gradient"
              gradient={{ from: 'brand.6', to: 'sun.5', deg: 35 }}
              fullWidth
              onClick={() => onUpgrade('tourer')}
              loading={busy}
            >
              Unlock Smart Match
            </Button>
          ) : currentTier === 'world_tourer' ? null : null
        }
      />
      <TierCard
        name="World Tourer"
        price="$19"
        priceDetail="/month"
        isCurrent={currentTier === 'world_tourer' && isActive}
        features={[
          'Everything in Tourer',
          'Multi-country Smart Touring',
          'International venue matching',
          'Priority support',
        ]}
        action={
          currentTier !== 'world_tourer' || !isActive ? (
            <Button
              variant="gradient"
              gradient={{ from: 'brand.6', to: 'sun.5', deg: 35 }}
              fullWidth
              onClick={() => onUpgrade('world_tourer')}
              loading={busy}
            >
              {currentTier === 'roamer'
                ? 'Go worldwide'
                : 'Upgrade to World Tourer'}
            </Button>
          ) : null
        }
      />
    </SimpleGrid>
  );
}

function TierCard({
  name,
  price,
  priceDetail,
  isCurrent,
  highlighted,
  features,
  action,
}: {
  name: string;
  price: string;
  priceDetail: string;
  isCurrent: boolean;
  highlighted?: boolean;
  features: string[];
  action: ReactNode;
}) {
  return (
    <Card
      withBorder
      padding="lg"
      radius="md"
      style={{
        borderColor: highlighted
          ? 'var(--mantine-color-brand-4)'
          : isCurrent
            ? 'var(--mantine-color-brand-6)'
            : undefined,
        borderWidth: highlighted || isCurrent ? 2 : undefined,
        position: 'relative',
      }}
    >
      <Stack gap="md" style={{ height: '100%' }}>
        <Box>
          <Group justify="space-between" align="center">
            <Title order={4}>{name}</Title>
            {isCurrent && (
              <Badge color="brand" variant="light" size="sm">
                Current plan
              </Badge>
            )}
          </Group>
          <Group gap={4} align="baseline" mt="xs">
            <Text size="xl" fw={700}>
              {price}
            </Text>
            <Text size="sm" c="dimmed">
              {priceDetail}
            </Text>
          </Group>
        </Box>

        <List size="sm" spacing="xs" style={{ flex: 1 }}>
          {features.map((f) => (
            <List.Item key={f}>{f}</List.Item>
          ))}
        </List>

        {action}
      </Stack>
    </Card>
  );
}

function SubscriptionDetails({
  sub,
  onCancel,
  onResume,
  cancelBusy,
  resumeBusy,
}: {
  sub: ArtistSubscriptionDto;
  onCancel: () => void;
  onResume: () => void;
  cancelBusy: boolean;
  resumeBusy: boolean;
}) {
  const tierLabel = sub.tier === 'world_tourer' ? 'World Tourer' : 'Tourer';

  const stateBadge =
    sub.state === 'active'
      ? { color: 'teal', label: 'Active' }
      : sub.state === 'trial'
        ? { color: 'blue', label: 'Trial' }
        : sub.state === 'past_due'
          ? { color: 'yellow', label: 'Past due' }
          : sub.state === 'paused'
            ? { color: 'gray', label: 'Paused' }
            : { color: 'red', label: 'Cancelled' };

  return (
    <Card withBorder padding="lg" radius="md">
      <Stack gap="sm">
        <Group justify="space-between">
          <Text fw={600} size="lg">
            {tierLabel} plan
          </Text>
          <Badge color={stateBadge.color} variant="light">
            {stateBadge.label}
          </Badge>
        </Group>

        <Text size="sm" c="dimmed">
          {formatMoney(sub.priceAmountMinor, sub.priceCurrency)} /{' '}
          {sub.billingCycle === 'annual' ? 'year' : 'month'}
        </Text>

        {sub.currentPeriodEnd && (
          <Text size="sm" c="dimmed">
            Current period ends: {formatDate(sub.currentPeriodEnd)}
          </Text>
        )}

        {sub.nextChargeAt && sub.state === 'active' && (
          <Text size="sm" c="dimmed">
            Next charge: {formatDate(sub.nextChargeAt)}
          </Text>
        )}

        {sub.cancelledAt && (
          <Text size="sm" c="dimmed">
            Cancelled on {formatDate(sub.cancelledAt)}
            {sub.cancellationReason ? `. ${sub.cancellationReason}` : ''}
          </Text>
        )}

        <Group mt="sm">
          {(sub.state === 'active' || sub.state === 'trial') && (
            <Button
              color="red"
              variant="outline"
              onClick={onCancel}
              loading={cancelBusy}
            >
              Cancel plan
            </Button>
          )}
          {(sub.state === 'cancelled' || sub.state === 'paused') && (
            <Button
              variant="gradient"
              gradient={{ from: 'brand.6', to: 'sun.5', deg: 35 }}
              onClick={onResume}
              loading={resumeBusy}
            >
              Resume plan
            </Button>
          )}
        </Group>
      </Stack>
    </Card>
  );
}

function formatMoney(minor: number, currency: string): string {
  const major = minor / 100;
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
  }).format(major);
}

function formatDate(ms: number): string {
  return new Date(ms).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}
